import { PaperCard } from "@/components/ui/paper-card";
import { Kicker } from "@/components/ui/kicker";
import { SeverityDot } from "@/components/ui/severity-dot";

export interface WorkspaceSidebarHighlightsProps {
  whatWorked: string[];
  openQuestions: string[];
  guidanceRules?: Array<{ id: string; rule: string; reason?: string | null }>;
}

export function WorkspaceSidebarHighlights({
  whatWorked,
  openQuestions,
  guidanceRules,
}: WorkspaceSidebarHighlightsProps) {
  const hasHighlights = whatWorked.length > 0 || openQuestions.length > 0;
  const hasGuidance = Boolean(guidanceRules && guidanceRules.length > 0);

  if (!hasHighlights && !hasGuidance) {
    return null;
  }

  return (
    <>
      {hasHighlights && (
        <PaperCard className="p-5 space-y-4 border border-border/80 text-xs">
          {whatWorked.length > 0 && (
            <div className="space-y-2">
              <Kicker as="h3" className="text-finding-helps font-semibold">
                What worked ({whatWorked.length})
              </Kicker>
              <ul className="space-y-1.5">
                {whatWorked.map((item, i) => (
                  <li
                    key={`${i}-${item.slice(0, 16)}`}
                    className="flex items-start gap-2"
                  >
                    <span className="shrink-0 mt-1">
                      <SeverityDot tone="minor" />
                    </span>
                    <span className="text-foreground leading-normal">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {openQuestions.length > 0 && (
            <div className="space-y-2 border-t border-border/60 pt-3">
              <Kicker as="h3">Open questions ({openQuestions.length})</Kicker>
              <ul className="space-y-1.5 list-disc list-inside text-muted-foreground">
                {openQuestions.map((item, i) => (
                  <li
                    key={`${i}-${item.slice(0, 16)}`}
                    className="leading-normal"
                  >
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </PaperCard>
      )}

      {hasGuidance && guidanceRules && (
        <PaperCard className="p-5 space-y-3 border border-border/80">
          <Kicker as="h3">Learned guidance</Kicker>
          <ul className="divide-y divide-border/60 text-xs">
            {guidanceRules.map((g) => (
              <li key={g.id} className="py-2.5 space-y-1">
                <p className="font-medium text-foreground">{g.rule}</p>
                {g.reason && (
                  <p className="text-muted-foreground">{g.reason}</p>
                )}
              </li>
            ))}
          </ul>
        </PaperCard>
      )}
    </>
  );
}
